<template>
    <div class="set-component">
        <div class="set-header">主题设置</div>
        <div class="set-body">
            <!-- 主题模式切换 -->
            <div class="section-title">外观模式</div>
            <div class="theme-list">
                <div
                    v-for="t in themes"
                    :key="t.value"
                    :class="['theme-item', { active: currentMode === t.value }]"
                    @click="selectMode(t.value)"
                >
                    <div class="preview" :style="t.previewStyle">
                        <span class="preview-emoji">{{ t.emoji }}</span>
                    </div>
                    <div class="label">{{ t.label }}</div>
                </div>
            </div>

            <!-- 主题色自定义 -->
            <div class="section-title" style="margin-top: 28px;">主题颜色</div>
            <div class="color-list">
                <div
                    v-for="c in presetColors"
                    :key="c.value"
                    :class="['color-item', { active: currentColor.toLowerCase() === c.value.toLowerCase() }]"
                    :style="{ background: c.value }"
                    :title="c.label"
                    @click="selectColor(c.value)"
                >
                    <i v-if="currentColor.toLowerCase() === c.value.toLowerCase()" class="el-icon-check"></i>
                </div>
            </div>
            <div class="color-picker-row">
                <span class="color-picker-label">自定义：</span>
                <el-color-picker v-model="customColor" @change="onCustomColorChange" />
                <span class="color-value">{{ currentColor }}</span>
            </div>

            <!-- 说明 -->
            <div class="hint">
                <i class="el-icon-info"></i>
                主题设置会自动保存到本地；登录后将同步到服务器。
            </div>
        </div>
    </div>
</template>

<script>
import {
    THEME_KEY,
    THEME_COLOR_KEY,
    getThemeMode,
    getThemeColor,
    setThemeMode,
    setThemeColor,
    applyTheme,
    applyThemeColor,
    toggleTheme
} from '@/utils/theme';

export default {
    name: 'ThemeSetting',
    data() {
        return {
            currentMode: getThemeMode(),
            currentColor: getThemeColor(),
            customColor: getThemeColor(),
            themes: [
                {
                    value: 'light',
                    label: '明亮',
                    emoji: '☀️',
                    previewStyle: {
                        background: '#ffffff',
                        borderColor: '#e4e7ed'
                    }
                },
                {
                    value: 'dark',
                    label: '暗黑',
                    emoji: '🌙',
                    previewStyle: {
                        background: '#1a1a2e',
                        borderColor: '#2a2a4a'
                    }
                },
                {
                    value: 'system',
                    label: '跟随系统',
                    emoji: '🖥️',
                    previewStyle: {
                        background:
                            'linear-gradient(135deg, #ffffff 0%, #ffffff 50%, #1a1a2e 50%, #1a1a2e 100%)',
                        borderColor: '#909399'
                    }
                }
            ],
            presetColors: [
                { value: '#409EFF', label: '经典蓝' },
                { value: '#67C23A', label: '森林绿' },
                { value: '#E6A23C', label: '暖阳橙' },
                { value: '#F56C6C', label: '珊瑚红' },
                { value: '#909399', label: '中性灰' },
                { value: '#8e2de2', label: '紫罗兰' },
                { value: '#ff69b4', label: '樱花粉' },
                { value: '#00c4cc', label: '青色' },
                { value: '#ff9800', label: '橙色' },
                { value: '#3f51b5', label: '靛蓝' }
            ]
        };
    },
    mounted() {
        this.loadThemeSetting();
        this.$bus && this.$bus.$on('user-theme-loaded', this.handleUserThemeLoaded);
    },
    beforeDestroy() {
        this.$bus && this.$bus.$off('user-theme-loaded', this.handleUserThemeLoaded);
    },
    methods: {
        handleUserThemeLoaded(data) {
            // App.vue 已从后端加载完，标记自己不要重复加载
            this._skipBackendLoad = true;
        },
        getUserId() {
            try {
                const user = JSON.parse(localStorage.getItem('userInfo'));
                return user ? user.userId || user.id || null : null;
            } catch (e) {
                return null;
            }
        },
        async loadThemeSetting() {
            // 先从本地恢复
            this.currentMode = getThemeMode();
            this.currentColor = getThemeColor();
            this.customColor = this.currentColor;

            // 检查 App.vue 是否已经加载过后端数据（防止重复请求冲突）
            if (this._skipBackendLoad || localStorage.getItem('userThemeLoaded') === '1') {
                return;
            }

            // 登录状态下，尝试从后端读取最新设置（仅在未加载过的情况下）
            const userId = this.getUserId();
            if (!userId) return;
            try {
                const res = await this.$axios.get('/api/user/theme/get', {
                    params: { userId }
                });
                if (res && res.data && res.data.code === 200 && res.data.data) {
                    const data = res.data.data;
                    if (data.themeMode && ['light', 'dark', 'system'].indexOf(data.themeMode) >= 0) {
                        this.currentMode = data.themeMode;
                        setThemeMode(data.themeMode);
                    }
                    if (data.themeColor) {
                        this.currentColor = data.themeColor;
                        this.customColor = this.currentColor;
                        setThemeColor(data.themeColor);
                    }
                }
            } catch (e) {
                // 后端读取失败不阻断 UI，仍使用本地设置
            }
        },
        async selectMode(mode) {
            if (this.currentMode === mode) return;
            this.currentMode = mode;
            setThemeMode(mode);
            this.$emit('change', { mode, color: this.currentColor });
            // 通过 $bus 通知其他组件主题已更新
            this.$bus && this.$bus.$emit('theme-updated', { mode, color: this.currentColor });
            await this.saveToBackend({ themeMode: mode });
            this.$message.success(`已切换到${this.themes.find(t => t.value === mode).label}模式`);
        },
        async selectColor(color) {
            if (this.currentColor.toLowerCase() === color.toLowerCase()) return;
            this.currentColor = color;
            this.customColor = color;
            setThemeColor(color);
            this.$emit('change', { mode: this.currentMode, color });
            // 通过 $bus 通知其他组件主题已更新
            this.$bus && this.$bus.$emit('theme-updated', { mode: this.currentMode, color });
            await this.saveToBackend({ themeColor: color });
        },
        onCustomColorChange(val) {
            if (!val) return;
            this.selectColor(val);
        },
        async saveToBackend(patch) {
            const userId = this.getUserId();
            if (!userId) return;
            try {
                await this.$axios.post('/api/user/theme/update', {
                    userId,
                    themeMode: patch.themeMode != null ? patch.themeMode : this.currentMode,
                    themeColor: patch.themeColor != null ? patch.themeColor : this.currentColor,
                    wallpaperType: 'default'
                });
            } catch (e) {
                // 保存失败不阻断 UI；localStorage 已在 setThemeMode/setThemeColor 中写入
                console.warn('同步主题设置到服务器失败', e);
            }
        },
        // 兼容旧调用（保留原 applyTheme 方法名，转发到新 API）
        applyTheme(mode, color) {
            if (mode) applyTheme(mode);
            if (color) applyThemeColor(color);
        },
        // 兼容旧调用
        toggleTheme() {
            const newMode = toggleTheme();
            this.currentMode = newMode;
            return newMode;
        }
    }
};
</script>

<style scoped>
.set-component {
    background: var(--card-background);
    border-radius: 12px;
    border: 1px solid var(--border-color-lighter);
}
.set-header {
    padding: 18px 24px;
    font-size: 16px;
    font-weight: 600;
    color: var(--text-color);
    border-bottom: 1px solid var(--border-color-lighter);
}
.set-body {
    padding: 24px;
}
.section-title {
    font-size: 14px;
    font-weight: 600;
    color: var(--text-color);
    margin-bottom: 14px;
}

/* 主题模式卡片 */
.theme-list {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
}
.theme-item {
    width: 120px;
    text-align: center;
    cursor: pointer;
    user-select: none;
}
.theme-item .preview {
    width: 100%;
    height: 88px;
    border-radius: 12px;
    margin: 0 auto 8px;
    border: 3px solid transparent;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: border-color 0.2s, transform 0.2s, box-shadow 0.2s;
    box-sizing: border-box;
}
.theme-item:hover .preview {
    transform: translateY(-2px);
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
}
.theme-item.active .preview {
    border-color: var(--theme-color, #409EFF);
    box-shadow: 0 0 0 2px var(--theme-color-light, rgba(64, 158, 255, 0.15));
}
.preview-emoji {
    font-size: 28px;
}
.theme-item .label {
    font-size: 13px;
    color: var(--text-color);
}

/* 预设颜色 */
.color-list {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
}
.color-item {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 18px;
    border: 2px solid transparent;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
    transition: transform 0.2s, box-shadow 0.2s, border-color 0.2s;
}
.color-item:hover {
    transform: scale(1.1);
}
.color-item.active {
    border-color: #fff;
    box-shadow: 0 0 0 2px var(--theme-color, #409EFF);
}

/* 自定义颜色 */
.color-picker-row {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 16px;
    color: var(--text-color-secondary);
    font-size: 13px;
}
.color-picker-label {
    color: var(--text-color);
}
.color-value {
    color: var(--text-color-muted);
    font-family: Consolas, Monaco, monospace;
    font-size: 12px;
    letter-spacing: 0.5px;
}

/* 提示 */
.hint {
    margin-top: 20px;
    padding: 10px 14px;
    border-radius: 8px;
    background: var(--background-color-soft);
    color: var(--text-color-muted);
    font-size: 12px;
    line-height: 1.6;
    display: flex;
    align-items: flex-start;
    gap: 8px;
}
.hint i {
    color: var(--theme-color);
    margin-top: 2px;
}
</style>
